import {
  Box,
  Button,
  Dialog,
  Backdrop,
  useTheme,
  TextField,
  DialogTitle,
  Autocomplete,
  DialogActions,
  DialogContent,
  svgIconClasses,
  CircularProgress,
  DialogContentText,
} from '@mui/material';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import { Permission } from 'library/types';
import apiService from 'library/api-service';
import { GridRowId } from '@mui/x-data-grid-premium';
import { renderAutoCompleteLocaleText } from 'utils';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormattedMessage, useIntl } from 'react-intl';
import { gray } from 'providers/theme/themePrimitives';
import { useEffect, useState, useCallback } from 'react';
import loaderLogoSrc from 'assets/images/idecon-loader-logo.png';

export type CreateRoleFormParams = {
  name: string;
  permissionCodes: string[];
};

const defaultValues: CreateRoleFormParams = {
  name: '',
  permissionCodes: [],
};

type CreateRoleDialogProps = {
  open: boolean;
  cloneRoleId?: GridRowId | null;
  handleClose: () => void;
  refresh: () => Promise<void>;
};

const CreateRoleDialog = ({ open, cloneRoleId, handleClose: closeDialog, refresh }: CreateRoleDialogProps) => {
  // other hooks
  const intl = useIntl();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  // useState hooks
  const [loading, setLoading] = useState(false);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [selectedPermissions, setSelectedPermissions] = useState<Permission[]>([]);

  // form validations & form state
  const schema = yup.object().shape({
    name: yup
      .string()
      .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' })),
    permissionCodes: yup
      .array()
      .of(
        yup
          .string()
          .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' }))
      )
      .min(1, intl.formatMessage({ id: 'validations.atLeastOneElement', defaultMessage: 'En az bir öğe seçilmelidir' }))
      .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' })),
  });

  const {
    reset,
    control,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateRoleFormParams>({
    defaultValues,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: yupResolver(schema),
    shouldFocusError: true,
  });

  // async helper functions
  const onSubmit = async (data: CreateRoleFormParams) => {
    try {
      setLoading(true);

      const response = await apiService.createRole(data);

      if (response.isSuccess) {
        enqueueSnackbar(
          <FormattedMessage
            id="roles.createRoleCompleted"
            defaultMessage="Rol oluşturma işlemi başarıyla tamamlandı."
          />,
          { variant: 'success' }
        );
        handleClose();
        refresh();
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const getPermissions = async () => {
    try {
      setLoadingPermissions(true);

      const response = await apiService.getPermissions();

      if (response.isSuccess) {
        setPermissions(response.data);
      }
    } catch {
    } finally {
      setLoadingPermissions(false);
    }
  };

  const getRoleInfos = useCallback(async () => {
    try {
      setLoading(true);

      const response = await apiService.getSpecificRole(cloneRoleId as number);

      if (response.isSuccess) {
        let sPermissions: Permission[] = [];
        response.data.permissionCodes.forEach((permissionCode: string) => {
          const permission = permissions.find((permission) => permission.code === permissionCode);

          if (permission) {
            sPermissions.push(permission);
          }
        });

        setValue(
          'name',
          `${response.data.name}${intl.formatMessage({ id: 'roles.copySuffix', defaultMessage: ' (Kopya)' })}`
        );
        setValue('permissionCodes', response.data.permissionCodes);

        setSelectedPermissions(sPermissions);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  }, [permissions, cloneRoleId, setValue, intl]);

  // sync helper functions
  const handleClose = () => {
    closeDialog();
    reset();
    setSelectedPermissions([]);
  };

  // useEffect hooks
  useEffect(() => {
    if (open) {
      getPermissions();
    }
  }, [open]);

  useEffect(() => {
    if (open && cloneRoleId && permissions.length) {
      getRoleInfos();
    }
  }, [open, cloneRoleId, permissions, getRoleInfos]);

  return (
    <Dialog maxWidth="lg" open={open} onClose={handleClose}>
      <DialogTitle>
        <FormattedMessage id="roles.createRole" defaultMessage="Rol Oluştur" />
      </DialogTitle>
      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
          <DialogContentText>
            <FormattedMessage
              id="roles.createRoleText"
              defaultMessage="İlgili alanları girip devam ederek yeni bir rol oluşturabilirsiniz."
            />
          </DialogContentText>
          <Controller
            name="name"
            control={control}
            render={({ field: { ref, ...rest } }) => (
              <TextField
                {...rest}
                label={<FormattedMessage id="roles.name" defaultMessage="Adı" />}
                fullWidth
                size="small"
                error={!!errors.name}
                helperText={errors.name?.message}
              />
            )}
          />
          <Autocomplete
            multiple
            filterSelectedOptions
            size="small"
            options={permissions}
            value={selectedPermissions}
            loading={loadingPermissions}
            disableCloseOnSelect
            {...renderAutoCompleteLocaleText()}
            onChange={(_, newValue: Permission[]) => {
              setSelectedPermissions(newValue);

              const codes = newValue.map((value) => value.code);
              setValue('permissionCodes', codes);
            }}
            getOptionLabel={(option: Permission) =>
              intl.formatMessage({ id: `permissions.${option.code}`, defaultMessage: option.name })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                sx={{
                  '& .MuiButtonBase-root': {
                    border: 'none',
                    width: 'fit-content',
                    height: '2.25rem',
                    padding: '0.25rem',
                    [`& .${svgIconClasses.root}`]: { fontSize: '1rem' },
                    '&:hover': {
                      backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[800],
                    },
                  },
                  '& .MuiInputBase-root': {
                    height: 'auto',
                  },
                }}
                multiline
                variant="outlined"
                fullWidth
                size="small"
                label={<FormattedMessage id="roles.permissions" defaultMessage="İzinler" />}
                error={!!errors.permissionCodes}
                helperText={errors.permissionCodes?.message}
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingPermissions ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            )}
          />
        </DialogContent>
        <DialogActions sx={{ pb: 3, px: 3 }}>
          <Button onClick={handleClose}>
            <FormattedMessage id="forgotPassword.cancel" defaultMessage="Vazgeç" />
          </Button>
          <Button variant="contained" type="submit">
            <FormattedMessage id="forgotPassword.continue" defaultMessage="Devam" />
          </Button>
        </DialogActions>
      </Box>
      <Backdrop
        sx={{ display: 'flex', flexDirection: 'column', color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}>
        <img src={loaderLogoSrc} alt="loader-logo" />
        <CircularProgress color="inherit" />
      </Backdrop>
    </Dialog>
  );
};

export default CreateRoleDialog;
